import { useEffect, useState } from 'react';
import {
  apiClient,
  ApiError,
  type EventSummary,
  type EventSeverity,
  type TaskPriority,
} from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { RelativeTime } from '@/components/RelativeTime';

interface Props {
  projectId: string;
  /** Bumped by parent after a task is created so the task list refetches. */
  onConverted?: () => void;
}

const SEVERITY_COLOR: Record<EventSeverity, string> = {
  info: 'oklch(0.72 0.14 235)',
  warn: 'oklch(0.78 0.17 65)',
  urgent: 'oklch(0.62 0.22 25)',
};

const SEVERITY_TO_PRIORITY: Record<EventSeverity, TaskPriority> = {
  urgent: 'p0',
  warn: 'p1',
  info: 'p2',
};

const COLLAPSE_KEY_PREFIX = 'apetech-pm:project-inbox-collapsed:';

/**
 * Per-project inbox section. Surfaces events that were routed to this
 * project (via routing rules) but haven't been converted to a task yet.
 * Lets the user triage inline — Convert (one-click since project is
 * known), Mark Seen, Mark Done — instead of bouncing to the global
 * Inbox tab.
 *
 * 10s polling, gated on visibility. Auto-hides when there's nothing to
 * triage so it doesn't add clutter to a quiet project.
 */
export function ProjectInbox({ projectId, onConverted }: Props) {
  const [items, setItems] = useState<EventSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // event_id of in-flight action
  const collapseKey = `${COLLAPSE_KEY_PREFIX}${projectId}`;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(collapseKey) === '1';
  });

  useEffect(() => {
    window.localStorage.setItem(collapseKey, collapsed ? '1' : '0');
  }, [collapseKey, collapsed]);

  async function refresh(silent = false) {
    try {
      const r = await apiClient.listEvents({
        project_id: projectId,
        unconverted: true,
        status: 'new',
        limit: 50,
      });
      setItems(r.events);
      if (!silent) setErr(null);
    } catch (e) {
      if (!silent) setErr(e instanceof ApiError ? e.message : 'Failed to load inbox');
    }
  }

  useEffect(() => {
    refresh();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') refresh(true);
    }, 10000);
    function onVis() {
      if (document.visibilityState === 'visible') refresh(true);
    }
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function convert(evt: EventSummary) {
    setBusy(evt.event_id);
    setErr(null);
    try {
      await apiClient.convertEventToTask(evt.event_id, {
        project_id: projectId,
        priority: SEVERITY_TO_PRIORITY[evt.severity],
      });
      // Optimistic: drop the row from the list
      setItems((rows) => (rows ? rows.filter((r) => r.event_id !== evt.event_id) : rows));
      onConverted?.();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Convert failed');
    } finally {
      setBusy(null);
    }
  }

  async function setStatus(evt: EventSummary, status: 'seen' | 'done') {
    setBusy(evt.event_id);
    setErr(null);
    try {
      await apiClient.updateEvent(evt.event_id, { status });
      // Drop from list (we only show status='new' here)
      setItems((rows) => (rows ? rows.filter((r) => r.event_id !== evt.event_id) : rows));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Update failed');
    } finally {
      setBusy(null);
    }
  }

  // Hide entirely when there's nothing to triage — keeps quiet projects clean.
  if (items !== null && items.length === 0) return null;

  return (
    <section
      aria-labelledby="project-inbox-heading"
      className="section-glow rounded-lg bg-card/30"
    >
      <header className="flex items-center justify-between border-b border-border/60 px-4 py-2">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-expanded={!collapsed}
          aria-controls="project-inbox-body"
          className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
        >
          <span aria-hidden style={{ transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)' }}>
            ▸
          </span>
          <span id="project-inbox-heading">Inbox</span>
          {items && items.length > 0 && (
            <>
              <span className="text-muted-foreground/60">·</span>
              <span className="tabular-nums">{items.length}</span>
              <span className="ml-1 inline-flex size-1.5 animate-pulse rounded-full bg-[color:var(--glow-color)]" aria-hidden />
            </>
          )}
        </button>
        <span className="font-mono text-[10px] text-muted-foreground/60">events routed here</span>
      </header>

      {!collapsed && (
        <ul id="project-inbox-body" role="list" className="divide-y divide-border/40">
          {items === null ? (
            <li className="px-4 py-3" aria-busy="true">
              <div className="h-5 animate-pulse rounded bg-background/40" />
            </li>
          ) : (
            items.map((e) => (
              <li key={e.event_id} className="group/evt px-4 py-3">
                <div className="flex items-start gap-3">
                  <span
                    className="mt-1.5 size-1.5 shrink-0 rounded-full"
                    aria-hidden
                    style={{ background: SEVERITY_COLOR[e.severity] }}
                    title={`Severity: ${e.severity}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-2 text-sm">
                      <span className="truncate font-medium text-foreground">{e.title}</span>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60">
                        {e.source_name} · {e.type}
                      </span>
                      <RelativeTime ts={e.created_at} />
                    </div>
                    {e.body_md && (
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {e.body_md.slice(0, 160)}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1 self-start">
                    <button
                      type="button"
                      onClick={() => convert(e)}
                      disabled={busy === e.event_id}
                      className="rounded-md bg-primary px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
                      title="Convert to task (P0/P1/P2 by severity)"
                    >
                      → task
                    </button>
                    <button
                      type="button"
                      onClick={() => setStatus(e, 'seen')}
                      disabled={busy === e.event_id}
                      className="rounded px-1.5 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70 transition-colors hover:text-foreground disabled:opacity-40"
                      title="Mark seen"
                    >
                      seen
                    </button>
                    <button
                      type="button"
                      onClick={() => setStatus(e, 'done')}
                      disabled={busy === e.event_id}
                      className="rounded px-1.5 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70 transition-colors hover:text-foreground disabled:opacity-40"
                      title="Mark done (resolves without creating a task)"
                    >
                      done
                    </button>
                    {e.url && (
                      <a
                        href={e.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          'rounded px-1.5 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70 transition-colors hover:text-primary',
                        )}
                        title="Open source"
                      >
                        ↗
                      </a>
                    )}
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      )}

      {err && (
        <div
          role="alert"
          className="border-t border-destructive/40 bg-destructive/10 px-4 py-2 text-xs text-foreground"
        >
          {err}
        </div>
      )}
    </section>
  );
}
